import React, { ChangeEvent, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Dialog, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Button } from '../Button';
import { TextField } from '../TextField';
import { ErrorField, FileTitle, PickedImageWrapper, Preview, ReportBugButtonsWrapper, ReportBugGridContainer, TrashIcon, UploadButton } from './ReportBug.styles';
import { useInfoBarContext } from '../../Contexts/InfoBarContext';
import { IReportBug } from './RetportBug.types';
import { IBugReport, reportBug } from './API/BugReport.api';
import { useLocation, useParams } from 'react-router-dom';

const useSearchParams = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  const getParam = (paramName: string): string | null => {
    return searchParams.get(paramName);
  };
  
  return { getParam };
};

const MAX_FILE_SIZE = 1024*1024*5;

export const ReportBug = ({ handleClose }: IReportBug) => {
  const { showInfoBar } = useInfoBarContext();
  const { user } = useAuth0();
  const param = useParams<{id: string}>();
  const { getParam} = useSearchParams();
  const id =  param.id || getParam('verificationId');
  
  const [explanationNoteConntent, setExplanationNoteConntent] = useState('');
  const [title, setTitle] = useState('');
  const [isEmpty, setEmpty] = useState({explanationNote: false, title: false});
  const [selectedFileBase64, setSelectedFileBase64] = useState<string>();
  const [selectedFileMime, setSelectedFileMime] = useState<string>();
  const [pickedFile, setFilePicked] = useState<string>();
  const [fileName, setFileName] = useState<string>();
  const [fileError, setImageError] = useState('');

  const [reportType, setReportType] = useState<string | false>(false);
  const [helperText, setHelperText] = useState('');

  const handleSend = async () => {
    if(!explanationNoteConntent || !reportType || !title){
      !reportType && setHelperText('This field is required');
      !explanationNoteConntent && setEmpty((prev) => ({...prev, explanationNote: true}));
      !title && setEmpty((prev) => ({...prev, title: true}));
      return;
    }
    if(user?.email && explanationNoteConntent){
      const Data = await prepareDataToSend();
      if(Data){
        await reportBug(Data);
        showInfoBar('errorReport', 'Bug report sent!');
      }
      handleClose();
    }
  };

  const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const newSelectedFile = event.target.files;
    if (newSelectedFile?.length) {
      if(newSelectedFile[0].size > MAX_FILE_SIZE){
        setImageError('Your file is too big, please upload a smaller file. Max file size is 5 MB.');
      } else if(newSelectedFile) {
        const reader = new FileReader();
        setImageError('');
        reader.onloadend = () => {
          const splitedResult = String(reader.result).split(';');
          const mime = splitedResult[0].replace('data:', '');
          const base64 = splitedResult[1].replace('base64,', '');
          setSelectedFileBase64(base64);
          setSelectedFileMime(mime);
        };
        setFileName(newSelectedFile[0].name);
        reader.readAsDataURL(newSelectedFile[0]);
      }
    }
  };
  const removeFile = () => {
    if(selectedFileBase64){
      setSelectedFileBase64(undefined);
    }
    if(selectedFileMime){
      setSelectedFileMime(undefined);
    }
  };

  const prepareDataToSend = async (): Promise<IBugReport | undefined> => {
    if(user?.email && reportType){
      const dataToSend = {
        type: reportType,
        title,
        user: user?.email,
        description: id ? `<verificationId: ${id}>\n${explanationNoteConntent}` : explanationNoteConntent,
        url: window.location.href,
        fileBase64: selectedFileBase64 ? selectedFileBase64 : undefined,
        fileMime: selectedFileMime ? selectedFileMime : undefined,
        solved: false,
        creationDate: (new Date()).toISOString(),
      };
      return dataToSend;
    } 
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportType((event.target as HTMLInputElement).value);
    setHelperText(' ');
  };

  const handlePreview = () => {
    if(selectedFileBase64 && selectedFileMime){
      const src  = `data:${selectedFileMime};base64,${selectedFileBase64}`;
      setFilePicked(src);
    }
  };

  const hanleClose = () => setFilePicked(undefined);

  const isTypePdf = () => selectedFileMime === 'application/pdf';

  return(
    <div style={{minHeight:'220px', padding: '25px'}}>
      <ReportBugGridContainer container>
        <Grid item>
          <Typography fontWeight='bold' sx={{ml: 1}}>
            Select bug type*
          </Typography>
          <RadioGroup 
            onChange={handleRadioChange}
            value={reportType}
            sx={{flexDirection: 'row', ml: 2}}>
            <FormControlLabel  value="Data error" control={<Radio />} label="Data error" />
            <FormControlLabel value="Technical bug" control={<Radio />} label="Technical bug" />
            <FormControlLabel value="New Form Question" control={<Radio />} label="New Form Question" />
          </RadioGroup>
          <Typography sx={{ml: 2, fontSize: 12, color:'red'}}>{helperText}</Typography>
          <TextField
            label='Title*'
            size='large'
            error={isEmpty.title ? 'This field is required' : undefined} 
            onBlur={e => {
              setTitle(e as string);
              if ((e as string || '').trim()) {
                setEmpty((prev) => ({...prev, title: false}));
              } else {
                setEmpty((prev) => ({...prev, title: true}));
              }
            }}/>
          <TextField
            rows={4}
            multiline
            label='Please add some explanation note*' 
            error={isEmpty.explanationNote ? 'This field is required' : undefined} 
            onBlur={e => {
              setExplanationNoteConntent(e as string);
              setEmpty((prev) => ({...prev, explanationNote: false}));
            }}/>
          <div style={{margin: '0 0 10px 15px'}}>
            {
              selectedFileBase64 && 
                  <PickedImageWrapper >
                    { isTypePdf() ? <PictureAsPdfIcon/> :
                      <ImageIcon />}
                    <FileTitle variant="subtitle2">{fileName}</FileTitle>
                    <TrashIcon onClick={removeFile}/>
                    <Preview onClick={handlePreview}/>
                  </PickedImageWrapper>
               
            }
          </div>
        </Grid>
        {fileError && <ErrorField>
          {fileError}
        </ErrorField>}
        <ReportBugButtonsWrapper item>
          <input 
            type='file' 
            id="file-button" 
            style={{ display: 'none' }}  
            accept=".png, .jpg, .jpeg, .bmp, .pdf" 
            name="file" 
            onChange={changeHandler} />
          <label htmlFor="file-button">
            <UploadButton >
                Upload File <UploadFileIcon/>
            </UploadButton>
          </label>
          <div>
            <Button type='secondary' onClick={handleClose}>Cancel</Button>
            <Button type='primary' onClick={handleSend}>Send</Button>
          </div>
        </ReportBugButtonsWrapper>
      </ReportBugGridContainer>
      <Dialog
        open={!!pickedFile}
        onClose={hanleClose}
        sx={{alignItems: 'center'}}
      >
        { pickedFile && (
          <>
            { isTypePdf() ? 
              <iframe src={pickedFile} width='600px' height='850px' />
              : <img src={pickedFile} width='100%'/>}
            <Typography justifyContent='center' display='flex'>
              {fileName}
            </Typography>
          </>
        ) }
      </Dialog>
    </div>
  );
};
